<template>
  <v-dialog
    v-if="popup"
    v-model="visible"
    max-width="520"
    :fullscreen="isPhone"
    :transition="isPhone ? 'dialog-bottom-transition' : 'dialog-transition'"
  >
    <v-card>
      <v-toolbar flat dense color="transparent" class="pr-2">
        <v-spacer></v-spacer>
        <v-btn icon @click="dismiss()">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="pt-0">
        <v-img
          v-if="popup.photoPath"
          :src="baseurl + '/img/' + popup.photoPath"
          height="220"
          contain
          class="homepage-popup__image"
        ></v-img>
        <div v-else class="homepage-popup__image homepage-popup__image--empty d-flex align-center justify-center">
          <v-icon size="56" color="primary">mdi-calendar-star</v-icon>
        </div>

        <div class="text-overline mt-3" style="color:#560354" v-if="popup.date">Upcoming Event</div>
        <div class="text-h6 mb-2">{{ popup.heading }}</div>

        <div class="d-flex align-center mb-1" v-if="popup.date">
          <v-icon left small>mdi-calendar</v-icon>
          <span>{{ prettyDate(popup.date) }}</span>
        </div>
        <div class="d-flex align-center mb-2" v-if="popup.location">
          <v-icon left small>mdi-map-marker</v-icon>
          <span>{{ popup.location }}</span>
        </div>
        <div v-if="!popup.date && popup.body" class="mt-1">{{ popup.body }}</div>
      </v-card-text>

      <v-card-actions class="px-4 pb-4">
        <v-btn text @click="dismiss()">Not now</v-btn>
        <v-spacer></v-spacer>
        <!-- Internal event pages use Inertia's SPA navigation; a custom
             popup's link may point off-site, which Link cannot handle.
             Plain @click, not .native: Link is a functional component and
             drops native listeners, so clicks went uncounted. -->
        <Link v-if="isInternalLink" :href="popup.linkUrl" @click="onClickThrough()">
          <v-btn color="primary">{{ popup.buttonText }}</v-btn>
        </Link>
        <a v-else :href="popup.linkUrl" @click="onClickThrough()" style="text-decoration:none">
          <v-btn color="primary">{{ popup.buttonText }}</v-btn>
        </a>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from 'axios'
import { Link } from '@inertiajs/inertia-vue'

// Kept in step with VuetifyLayout's login dialog delay (60s) and the
// motivational-message snackbar (5s cookie-gated) — this sits between
// the two so it does not compete with either for attention.
const SHOW_AFTER_MS = 2500
const COOKIE_NAME = 'homepagePopupSeen'
const COOKIE_DAYS = 1

export default {
  name: 'HomepagePopup',
  components: { Link },
  props: {
    // { id, heading, body, photoPath, buttonText, linkUrl, date, location }
    popup: Object,
    baseurl: String,
  },
  data() {
    return {
      visible: false,
    }
  },
  computed: {
    isPhone() {
      return this.$vuetify.breakpoint.xsOnly
    },
    isInternalLink() {
      return !!this.popup && this.popup.linkUrl.startsWith('/')
    },
  },
  mounted() {
    if (!this.popup || this.hasBeenSeen()) return

    setTimeout(() => {
      this.visible = true
      // Marked as seen the moment it is shown, not only on dismiss —
      // otherwise a visitor who lets it sit open and navigates away
      // (or just refreshes) would be shown it again within the same day.
      this.setCookie(COOKIE_NAME, this.popup.id, COOKIE_DAYS)
      this.recordImpression()
    }, SHOW_AFTER_MS)
  },
  methods: {
    prettyDate(x) {
      const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }
      return new Date(x).toLocaleDateString(undefined, options)
    },
    hasBeenSeen() {
      return this.getCookie(COOKIE_NAME) === String(this.popup.id)
    },
    dismiss() {
      this.visible = false
    },
    onClickThrough() {
      axios.post(this.baseurl + '/popup/' + this.popup.id + '/click').catch(() => {})
    },
    recordImpression() {
      axios.post(this.baseurl + '/popup/' + this.popup.id + '/impression').catch(() => {})
    },
    getCookie(name) {
      const value = `; ${document.cookie}`
      const parts = value.split(`; ${name}=`)
      return parts.length !== 2 ? undefined : parts.pop().split(';').shift()
    },
    setCookie(name, value, expiryDays) {
      const exdate = new Date()
      exdate.setDate(exdate.getDate() + expiryDays)
      document.cookie = [`${name}=${value}`, `expires=${exdate.toUTCString()}`, 'path=/'].join(';')
    },
  },
}
</script>

<style scoped>
.homepage-popup__image {
    background: #f5f5f5;
    border-radius: 4px;
}
.homepage-popup__image--empty {
    border: 1px dashed rgba(86, 3, 84, 0.25);
}
</style>
